import { Component, OnInit, ViewChild, HostListener, OnDestroy, AfterViewInit, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, ElementRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ICountry } from "../../interfaces/icountry";
import { IDashboard, IDropDown, IDropDownItem, CountryDashboardInformation } from "../../interfaces/idashboard";
import { DropDownItem } from "../../models/dropdown";
import { DashboardService } from "../../services/dashboard.service";
import { CountryDashboardService } from "../../services/countrydashboard.service";
import { DashboardKeysComponent } from "../dashboardkeys/dashboardkeys.component";
import { ConfirmationModalComponent } from "../confirmationmodal/confirmationmodal.component";
import { ICanComponentDeactivate } from "../../interfaces/icancomponentdeactivate";
import { Observable } from "rxjs";
import { NgSelectComponent } from '../shared/ng-select/ng-select.component';

/**
 * A component works on the country selection.
 * This component can update category detail content and dashbaord details depending the selected country.
 */
@Component({
    selector: "mapcontent",
    templateUrl: "./mapcontent.component.html",
    styles: [`
        /* All custom styles for ng-select and dropdown-search-wrapper have been removed. */
        
        .dropdown-options {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: white;
            border: 1px solid #d1d3d4;
            border-top: none;
            border-radius: 0;
            max-height: 200px;
            overflow-y: auto;
            z-index: 10000;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        
        /* Stronger selector to override CKEditor and global styles */
        .map-content-component .dropdown-options {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: white;
            border: 1px solid #d1d3d4;
            border-top: none;
            border-radius: 0;
            max-height: 200px;
            overflow-y: auto;
            z-index: 1051 !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        body.modal-open .map-content-component .dropdown-options {
            z-index: 1041 !important;
        }
        
        .dropdown-option {
            padding: 6px 12px;
            cursor: pointer;
            text-align: left;
            color: #333;
            background-color: white;
            transition: background-color;
        }
        
        .dropdown-option:last-child {
            border-bottom: none;
        }
        
        .dropdown-option:hover {
            background-color: #0076A8;
            color: white;
        }
        
        .dropdown-option.no-results {
            color: #6c757d;
            font-style: italic;
            cursor: default;
            background-color: #f8f9fa;
        }
        
        .dropdown-option.no-results:hover {
            background-color: #f8f9fa;
            color: #6c757d;
        }
        
        /* Remove default form-control styling that makes it look disabled */
        .dropdown-search-input[readonly] {
            background-color: white !important;
            opacity: 1 !important;
            color: #333 !important;
            cursor: pointer !important;
        }
        
        /* Square input styling with increased width */
        .form-control.dropdown-search-input {
            height: 38px;
            line-height: 1.5;
            border-radius: 0;
            width: 100%;
            min-width: 350px;
            -webkit-appearance: none !important;
            -moz-appearance: none !important;
            appearance: none !important;
            background-image: none !important;
        }
        
        /* Remove all form-control arrows */
        .form-control.dropdown-search-input::-webkit-outer-spin-button,
        .form-control.dropdown-search-input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
        
        .form-control.dropdown-search-input::-ms-expand {
            display: none;
        }
        
        /* Responsive width adjustments */
        @media (min-width: 768px) {
            .dropdown-search-input {
                min-width: 400px;
            }
        }
        
        @media (min-width: 992px) {
            .dropdown-search-input {
                min-width: 450px;
            }
        }
        
        @media (min-width: 1200px) {
            .dropdown-search-input {
                min-width: 500px;
            }
        }
        
        /* Right-align the ng-select dropdown and its controls in mapcontent */
        .map-content-component .dropdown-search-wrapper {
            display: inline-block;
            width: auto;
            min-width: unset;
            max-width: 100%;
        }
        .map-content-component ng-select {
            min-width: 350px;
            width: auto;
            max-width: 100%;
        }
        .map-content-component .search-input-container {
            display: flex;
            align-items: center;
            position: relative;
        }
        .map-content-component .clear-button {
            right: 36px !important;
            left: auto !important;
        }
        .map-content-component .dropdown-arrow {
            right: 8px !important;
            left: auto !important;
        }
        /* Removed all .search-input, .form-control.search-input, .searchable-select-container .search-input.form-control, and parent container width/flex/padding rules */
    `],
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, DashboardKeysComponent, ConfirmationModalComponent, NgSelectComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class MapContentComponent implements OnInit, ICanComponentDeactivate, OnDestroy, AfterViewInit {
    countries: DropDownItem[] = new Array();
    filteredCountries: DropDownItem[] = new Array();
    allCountries: ICountry[] = new Array();
    selectedCountry: DropDownItem = null;
    previouslySelectedCountry: DropDownItem[] = new Array();
    showDropdownOptions: boolean = false;
    searchTerm: string = '';
    isInputFocused: boolean = false;
    tempSelectedCountry: DropDownItem = null;

    // variables for dashboard admin
    dashboardContent: IDashboard;
    countryDashboardInformation: CountryDashboardInformation = null;
    textEditorConfig: {};
    @ViewChild("confirmModal") confirmModal;

    @ViewChild(DashboardKeysComponent)
    private dashboardKeysComponent: DashboardKeysComponent;

    constructor(private activatedRoute: ActivatedRoute,
        private dashboardService: DashboardService,
        private router: Router,
        private countryDashboardService: CountryDashboardService,
        private elementRef: ElementRef // <-- inject ElementRef
    ) {
    }

    /**
     * On initailse of an country selection component.
     * Here it fetches the events data to display.
     * It also initialise the form contents.
     */
    ngOnInit(): void {
        this.textEditorConfig = { removeButtons: "Source,Styles,Format,Image,Anchor,About,SpecialChar", removePlugins: "blockquote" };
        let countriesData: ICountry[] = this.activatedRoute.snapshot.data["countries"];
        this.countries = countriesData.map((e: ICountry) => new DropDownItem(e.id, e.name, null));
        this.filteredCountries = [...this.countries]; // Initialize filtered countries
        countriesData.map((e: ICountry) => { this.allCountries.push(e); });
        let savedCountryInfo: CountryDashboardInformation = this.countryDashboardService.getCountryDashboardInformation();
        if (savedCountryInfo != null) {
            // there is saved country dashboard information, so load page based on existing data
            // set selected country
            this.selectedCountry = this.countries.filter((c: DropDownItem) => c.id === savedCountryInfo.countryId)[0];
            this.previouslySelectedCountry.push(this.selectedCountry);
            this.countryDashboardInformation = savedCountryInfo;
            this.countrySelected(this.countries.filter((c: DropDownItem) => c.id === savedCountryInfo.countryId)[0]);
            // Don't clear the saved information immediately - keep it for potential restoration
        }
        this.clearSelection();

        // Add event listeners for modal events
        this.addModalEventListeners();
    }

    ngOnDestroy(): void {
        // Remove event listeners when component is destroyed
        this.removeModalEventListeners();
        
        // Clean up mutation observer
        if (this.linkObserver) {
            this.linkObserver.disconnect();
        }
        

    }

    private addModalEventListeners(): void {
        // Listen for when modals are hidden to restore CKEditor panels
        document.addEventListener('hidden.bs.modal', this.onModalHidden.bind(this));
    }

    private removeModalEventListeners(): void {
        document.removeEventListener('hidden.bs.modal', this.onModalHidden.bind(this));
    }

    private onModalHidden(): void {
        // Restore CKEditor panels when modal is closed
        this.restoreCKEditorPanels();
    }

    private restoreCKEditorPanels(): void {
        // Restore CKEditor dropdown panels
        const ckDropdownPanels = document.querySelectorAll('.ck-dropdown__panel');
        ckDropdownPanels.forEach(panel => {
            if (panel) {
                (panel as HTMLElement).style.display = '';
            }
        });

        // Restore CKEditor balloon panels
        const ckBalloonPanels = document.querySelectorAll('.ck-balloon-panel');
        ckBalloonPanels.forEach(panel => {
            if (panel) {
                (panel as HTMLElement).style.display = '';
            }
        });

        // Restore CKEditor tooltips
        const ckTooltips = document.querySelectorAll('.ck-tooltip');
        ckTooltips.forEach(tooltip => {
            if (tooltip) {
                (tooltip as HTMLElement).style.display = '';
            }
        });

        // Restore CKEditor list dropdowns
        const ckLists = document.querySelectorAll('.ck-list');
        ckLists.forEach(list => {
            if (list && list.parentElement && list.parentElement.classList.contains('ck-dropdown__panel')) {
                (list.parentElement as HTMLElement).style.display = '';
            }
        });
    }

    /**
     * Fires on selection of a country dropdown
     * @param event The selected country value.
     */
    countrySelected(event: DropDownItem): void {
        if (event) {
            this.selectedCountry = event;
            this.loadCountryDashboardInformation(null, null);
        } else {
            this.selectedCountry = null;
            this.countryDashboardInformation = null;
        }
    }

    /**
     * Handle search input for country dropdown
     * @param event The input event from the search field
     */
    onSearchInput(event: any): void {
        this.searchTerm = event.target.value;
        this.showDropdownOptions = true;
        this.filterCountries();
        
        // Clear selected country when user starts typing
        if (this.selectedCountry && this.searchTerm.trim() !== '') {
            this.tempSelectedCountry = this.selectedCountry;
            this.selectedCountry = null;
        }
    }

    /**
     * Filter countries based on search term
     */
    filterCountries(): void {
        if (!this.searchTerm || this.searchTerm.trim() === '') {
            this.filteredCountries = [...this.countries];
        } else {
            this.filteredCountries = this.countries.filter(country =>
                country.text.toLowerCase().includes(this.searchTerm.toLowerCase())
            );
        }
    }

    /**
     * Handle selection of a country from dropdown
     * @param country The selected country
     */
    selectCountry(country: DropDownItem): void {
        this.selectedCountry = country;
        this.tempSelectedCountry = null;
        this.searchTerm = '';
        this.showDropdownOptions = false;
        this.isInputFocused = false;
        this.countrySelected(country);
    }

    /**
     * Handle input blur event
     */
    onInputBlur(): void {
        setTimeout(() => {
            this.showDropdownOptions = false;
            this.isInputFocused = false;
            if (!this.selectedCountry && this.tempSelectedCountry) {
                this.selectedCountry = this.tempSelectedCountry;
                this.tempSelectedCountry = null;
                this.searchTerm = '';
            } else if (!this.selectedCountry) {
                this.searchTerm = '';
            }
        }, 150);
    }

    /**
     * Handle input click event
     */
    onInputClick(): void {
        if (this.selectedCountry) {
            this.tempSelectedCountry = this.selectedCountry;
            this.selectedCountry = null;
            this.searchTerm = '';
        }
        this.isInputFocused = true;
        this.showDropdownOptions = true;
        this.filterCountries();
    }

    /**
     * Handle input focus - clear selection when refocusing
     */
    onInputFocus(): void {
        if (this.selectedCountry) {
            this.tempSelectedCountry = this.selectedCountry;
            this.selectedCountry = null;
            this.searchTerm = '';
        }
        this.isInputFocused = true;
        this.showDropdownOptions = true;
    }

    /**
     * Handle keydown events for country dropdown
     * @param event The keyboard event
     */
    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.showDropdownOptions = false;
            this.isInputFocused = false;
            (event.target as HTMLInputElement).blur();
            
            // Restore temp selection if no country is selected
            if (!this.selectedCountry && this.tempSelectedCountry) {
                this.selectedCountry = this.tempSelectedCountry;
                this.tempSelectedCountry = null;
                this.searchTerm = '';
            } else if (!this.selectedCountry) {
                this.searchTerm = '';
            }
        }
    }

    /**
     * Clear selected country
     */
    clearSelection(): void {
        this.selectedCountry = null;
        this.tempSelectedCountry = null;
        this.searchTerm = '';
        this.isInputFocused = false;
        this.showDropdownOptions = false;
    }

    /**
     * Handle dashboard input click event
     * @param dropdown The dropdown object
     */
    onDashboardInputClick(dropdown: any): void {
        if (!dropdown.isSearching) {
            dropdown.isSearching = true;
            dropdown.searchTerm = '';
        }
        dropdown.showOptions = true;
        this.filterDashboardItems(dropdown);
    }

    /**
     * Handle country dropdown change event
     * @param event The change event from the select element
     */
    onCountryChange(event: any): void {
        const selectedId = event.target.value;
        if (selectedId) {
            const selectedCountry = this.countries.find(c => c.id == selectedId);
            this.countrySelected(selectedCountry);
        } else {
            this.countrySelected(null);
        }
    }

    /**
     * Handle dashboard dropdown change event
     * @param event The change event from the select element
     * @param dropdown The dropdown object
     */
    onDropdownChange(event: any, dropdown: IDropDown): void {
        const selectedId = event.target.value;
        if (selectedId && dropdown.dropDownItems) {
            const selectedItem = dropdown.dropDownItems.find(item => item.id == selectedId);
            this.onDashboardDropDownSelected(selectedItem, dropdown);
        }
    }

    /**
     * Handle search input for dashboard dropdown
     * @param event The input event from the search field
     * @param dropdown The dropdown object
     */
    onDashboardSearchInput(event: any, dropdown: any): void {
        dropdown.searchTerm = event.target.value;
        dropdown.isSearching = true;
        dropdown.showOptions = true;
        this.filterDashboardItems(dropdown);
    }

    /**
     * Handle focus event for dashboard dropdown input
     * @param dropdown The dropdown object
     */
    onDashboardInputFocus(dropdown: any): void {
        dropdown.isSearching = true;
        dropdown.searchTerm = '';
        dropdown.showOptions = true;
        this.filterDashboardItems(dropdown);
    }

    /**
     * Clear dashboard dropdown selection
     * @param dropdown The dropdown object
     */
    clearDashboardSelection(dropdown: any): void {
        dropdown.selectedDropDownItem = null;
        dropdown.tempSelectedItem = null;
        dropdown.searchTerm = '';
        dropdown.showOptions = false;
    }

    /**
     * Filter dashboard items based on search term
     * @param dropdown The dropdown object
     */
    filterDashboardItems(dropdown: any): void {
        if (!dropdown.searchTerm || dropdown.searchTerm.trim() === '') {
            dropdown.filteredItems = [...dropdown.dropDownItems];
        } else {
            dropdown.filteredItems = dropdown.dropDownItems.filter((item: any) =>
                item.text.toLowerCase().includes(dropdown.searchTerm.toLowerCase())
            );
        }
    }

    /**
     * Handle selection of a dashboard item from dropdown
     * @param item The selected item
     * @param dropdown The dropdown object
     */
    selectDashboardItem(item: any, dropdown: any): void {
        dropdown.selectedDropDownItem = item;
        dropdown.tempSelectedItem = null;
        dropdown.searchTerm = '';
        dropdown.isSearching = false;
        dropdown.showOptions = false;
        
        // Trigger the dropdown selection logic and save state
        this.onDashboardDropDownSelected(item, dropdown);
    }

    /**
     * Handle dashboard input blur event
     * @param dropdown The dropdown object
     */
    onDashboardInputBlur(dropdown: any): void {
        // Small delay to allow click events on dropdown options
        setTimeout(() => {
            dropdown.showOptions = false;
            if (!dropdown.selectedDropDownItem) {
                dropdown.searchTerm = '';
            }
            dropdown.isSearching = false;
        }, 150);
    }

    /**
     * Handle keydown events for dashboard dropdown
     * @param event The keyboard event
     * @param dropdown The dropdown object
     */
    onDashboardKeyDown(event: KeyboardEvent, dropdown: any): void {
        if (event.key === 'Escape') {
            dropdown.showOptions = false;
            (event.target as HTMLInputElement).blur();
            
            if (!dropdown.selectedDropDownItem) {
                dropdown.searchTerm = '';
            }
        }
    }

    /**
     * Fetch the country code for the selected country.
     */
    getCountryCode(): string {
        let retCountryCode: string = "";
        let filteredCountries: ICountry[] = this.allCountries.filter(c => c.id === this.selectedCountry.id);
        if (filteredCountries != null) {
            let country: ICountry = filteredCountries[0];
            retCountryCode = country.code;
        }
        return retCountryCode;
    }

    /**
     * Load all the country dashboard infromation.
     */
    loadCountryDashboardInformation(dashboardChanged: IDropDown, selectedItemId: IDropDownItem): void {
        const savedInfo = this.countryDashboardService.getCountryDashboardInformation();
        
        if (savedInfo == null || savedInfo.countryId !== this.selectedCountry.id) {
            // there is no saved country information for this country so load from scratch
            this.dashboardService.getDashboardContent(this.getCountryCode()).subscribe((d: IDashboard) => {
                this.dashboardContent = d;
                this.countryDashboardInformation = this.countryDashboardService.getCountryDashboardData(d.dropDowns,
                    dashboardChanged,
                    selectedItemId,
                    this.selectedCountry.id,
                    this.selectedCountry.text,
                    this.getCountryCode());
                this.initializeDashboardDropdowns();
                
                // Save the new dashboard information
                this.countryDashboardService.setCountryDashboardInformation(this.countryDashboardInformation, this.selectedCountry.id);
            });
        } else {
            // there is saved country information for this country
            this.dashboardService.getDashboardContent(this.getCountryCode()).subscribe((d: IDashboard) => {
                this.dashboardContent = d;
                this.countryDashboardInformation = savedInfo;
                this.initializeDashboardDropdowns();
            });
        }
    }

    /**
     * Initialize dashboard dropdowns with search functionality
     */
    initializeDashboardDropdowns(): void {
        if (this.countryDashboardInformation && this.countryDashboardInformation.dropDownsToRender) {
            this.countryDashboardInformation.dropDownsToRender.forEach((dropdown: any) => {
                if (dropdown.dropDownItems) {
                    dropdown.filteredItems = [...dropdown.dropDownItems];
                    dropdown.showOptions = false;
                    dropdown.searchTerm = '';
                    dropdown.isSearching = false;
                    
                    // Ensure selected item is properly initialized
                    if (dropdown.selectedDropDownItem) {
                        // Make sure the selected item is in the filtered items
                        const selectedExists = dropdown.filteredItems.find((item: any) => 
                            item.id === dropdown.selectedDropDownItem.id);
                        if (!selectedExists) {
                            dropdown.filteredItems.unshift(dropdown.selectedDropDownItem);
                        }
                    }
                }
            });
        }
    }

    onDashboardDropDownSelected(selectedItem: any, dropdown: IDropDown): void {
        this.countryDashboardInformation.dropDownsToRender =
            this.countryDashboardService.getDropDownHieararchy(this.dashboardContent.dropDowns, dropdown, selectedItem as IDropDownItem);
        
        // Re-initialize dropdowns after hierarchy change to ensure proper search functionality
        this.initializeDashboardDropdowns();
        
        // Save the updated country dashboard information
        this.countryDashboardService.setCountryDashboardInformation(this.countryDashboardInformation, this.selectedCountry.id);
    }

    saveClicked(): void {
        // Save current state before delegating to dashboard component
        if (this.countryDashboardInformation && this.selectedCountry) {
            this.countryDashboardService.setCountryDashboardInformation(this.countryDashboardInformation, this.selectedCountry.id);
        }
        
        this.dashboardKeysComponent.saveClicked();
        this.dashboardKeysComponent.dashboardKeysForm.markAsPristine();
    }

    resetComponent(): void {
        this.previouslySelectedCountry = null;
        this.selectedCountry = null;
    }

    showPublishModal(): void {
        // Close any open dropdowns before showing modal
        this.closeAllDropdowns();
        // Close any open CKEditor panels/dropdowns
        this.closeCKEditorPanels();
        this.dashboardKeysComponent.showPublishModal();
    }

    private closeAllDropdowns(): void {
        // Close country dropdown
        this.showDropdownOptions = false;
        
        // Close all dashboard dropdowns
        if (this.countryDashboardInformation && this.countryDashboardInformation.dropDownsToRender) {
            this.countryDashboardInformation.dropDownsToRender.forEach((dropdown: any) => {
                if (dropdown) {
                    (dropdown as any).showOptions = false;
                }
            });
        }
    }

    private closeCKEditorPanels(): void {
        // Check if link editing is currently active and skip closing if it is
        const activeLinkBalloons = document.querySelectorAll('.ck-balloon-panel:not([style*="display: none"])');
        let hasActiveLinkEditing = false;
        
        activeLinkBalloons.forEach(balloon => {
            const hasLinkForm = balloon.querySelector('.ck-link-form') || 
                               balloon.querySelector('.ck-link-form-view') ||
                               balloon.innerHTML.includes('Link URL');
            if (hasLinkForm) {
                hasActiveLinkEditing = true;
            }
        });
        
        // Close any open CKEditor dropdown panels
        const ckDropdownPanels = document.querySelectorAll('.ck-dropdown__panel');
        ckDropdownPanels.forEach((panel) => {
            if (panel) {
                (panel as HTMLElement).style.display = 'none';
            }
        });

        // Close CKEditor balloon panels, but preserve link editing balloons
        const ckBalloonPanels = document.querySelectorAll('.ck-balloon-panel');
        
        ckBalloonPanels.forEach((panel) => {
            if (panel) {
                // Check if this is a link editing balloon - preserve these for functionality
                const linkForm = panel.querySelector('.ck-link-form');
                const linkActions = panel.querySelector('.ck-link-actions');
                const linkUI = panel.querySelector('.ck-link-ui');
                const linkFormView = panel.querySelector('.ck-link-form-view');
                const linkActionsView = panel.querySelector('.ck-link-actions-view');
                const hasLinkContent = panel.innerHTML.includes('ck-link') || 
                                      panel.innerHTML.includes('Link URL') ||
                                      panel.innerHTML.includes('data-cke-tooltip-text="Edit link"') ||
                                      panel.innerHTML.includes('data-cke-tooltip-text="Unlink"');
                
                // More comprehensive link balloon detection
                const isLinkEditBalloon = linkForm || linkActions || linkUI || linkFormView || linkActionsView || hasLinkContent;
                
                // Only close non-link balloons
                if (!isLinkEditBalloon) {
                    (panel as HTMLElement).style.display = 'none';
                } else {
                    // Ensure it stays visible with proper positioning and z-index
                    const panelElement = panel as HTMLElement;
                    panelElement.style.display = '';
                    panelElement.style.visibility = 'visible';
                    panelElement.style.opacity = '1';
                    panelElement.style.zIndex = '2147483647'; // Ultra high z-index
                    panelElement.style.position = 'fixed'; // Ensure it's positioned properly
                    
                    // Force it to be visible and on top
                    panelElement.classList.add('ck-balloon-panel_visible');
                    panelElement.classList.remove('ck-balloon-panel_invisible');
                    
                    // Mark balloon with data attribute for CSS targeting
                    panelElement.setAttribute('data-map-component-balloon', 'true');
                }
            }
        });

        // Close any open CKEditor tooltips
        const ckTooltips = document.querySelectorAll('.ck-tooltip');
        ckTooltips.forEach((tooltip) => {
            if (tooltip) {
                (tooltip as HTMLElement).style.display = 'none';
            }
        });

        // Close any open CKEditor list dropdowns  
        const ckLists = document.querySelectorAll('.ck-list');
        ckLists.forEach((list) => {
            if (list && list.parentElement && list.parentElement.classList.contains('ck-dropdown__panel')) {
                (list.parentElement as HTMLElement).style.display = 'none';
            }
        });
    }

    ngAfterViewInit(): void {
        // Set up observer to detect when CKEditor instances are created dynamically
        this.observeCKEditorCreation();
    }

    private observeCKEditorCreation(): void {
        // Set up observer for dynamically created CKEditor instances
        if (!this.linkObserver) {
            this.linkObserver = new MutationObserver((mutations) => {
                mutations.forEach(mutation => {
                    mutation.addedNodes.forEach(node => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as Element;
                            
                            // Check for new CKEditor instances
                            if (element.matches && element.matches('.ck-editor')) {
                                setTimeout(() => this.setupCKEditorBalloonManagement(), 1000);
                            }
                            
                            // Check for new CKEditor instances in subtree
                            if (element.querySelectorAll) {
                                const editors = element.querySelectorAll('.ck-editor');
                                if (editors.length > 0) {
                                    setTimeout(() => this.setupCKEditorBalloonManagement(), 1000);
                                }
                            }
                            
                            // Also check for dashboardkeys component which contains CKEditor
                            if (element.matches && element.matches('app-dashboardkeys')) {
                                setTimeout(() => this.setupCKEditorBalloonManagement(), 2000);
                            }
                            
                            // Check for dashboardkeys in subtree
                            if (element.querySelectorAll) {
                                const dashboardKeys = element.querySelectorAll('app-dashboardkeys');
                                if (dashboardKeys.length > 0) {
                                    setTimeout(() => this.setupCKEditorBalloonManagement(), 2000);
                                }
                            }
                            
                            // Check for new balloon panels
                            if (element.matches && element.matches('.ck-balloon-panel')) {
                                const hasLinkContent = !!element.querySelector('.ck-link-form, .ck-link-actions') || 
                                                      element.innerHTML.includes('Link URL') ||
                                                      element.innerHTML.includes('Edit link') ||
                                                      element.innerHTML.includes('Unlink');
                                
                                // If this is a link-related balloon, ensure maximum visibility
                                if (hasLinkContent) {
                                    const balloonElement = element as HTMLElement;
                                    
                                    // Mark this balloon as belonging to map component for targeted CSS
                                    balloonElement.setAttribute('data-map-component-balloon', 'true');
                                    
                                    balloonElement.style.zIndex = '2147483647';
                                    balloonElement.style.visibility = 'visible';
                                    balloonElement.style.opacity = '1';
                                    balloonElement.style.display = 'block';
                                    balloonElement.style.position = 'fixed';
                                    
                                    // CRITICAL: Fix the balloon rotator z-index issue
                                    const rotator = balloonElement.querySelector('.ck-balloon-rotator') as HTMLElement;
                                    if (rotator) {
                                        rotator.style.zIndex = '2147483647';
                                    }
                                    
                                    // Fix all child elements z-index
                                    const allChildren = balloonElement.querySelectorAll('*');
                                    allChildren.forEach(child => {
                                        (child as HTMLElement).style.zIndex = '2147483647';
                                    });
                                    
                                    // Force position to center of screen if it's off-screen
                                    const rect = balloonElement.getBoundingClientRect();
                                    const isOffScreen = rect.top < 0 || rect.left < 0 || rect.top > window.innerHeight || rect.left > window.innerWidth;
                                    
                                    if (isOffScreen) {
                                        balloonElement.style.top = '50%';
                                        balloonElement.style.left = '50%';
                                        balloonElement.style.transform = 'translate(-50%, -50%)';
                                    }
                                    
                                    // Add visible class, remove hidden classes
                                    balloonElement.classList.add('ck-balloon-panel_visible');
                                    balloonElement.classList.remove('ck-balloon-panel_invisible');
                                    
                                    // Force a repaint
                                    balloonElement.offsetHeight;
                                }
                            }
                            

                        }
                    });
                });
            });

            this.linkObserver.observe(document.body, {
                childList: true,
                subtree: true
            });
            
        }
        
        // Also do initial check
        this.setupCKEditorBalloonManagement();
    }

    private setupCKEditorBalloonManagement(): void {
        // Find all CKEditor elements first
        const ckEditors = document.querySelectorAll('.ck-editor');
        
        // If no CKEditor instances found, exit early
        if (ckEditors.length === 0) {
            return;
        }
        

    }



    private linkObserver: MutationObserver;

    @HostListener("window:beforeunload")
    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        if (this.dashboardKeysComponent && this.dashboardKeysComponent.dashboardKeysForm.dirty) {
            this.confirmModal.openModal();
            return false;
        } else {
            return true;
        }
    }
    onAccept(): void {
        // reset the the original back to whats currenlty in itemConfigSet, this allows to pass check  in candeactivate
        this.dashboardKeysComponent.dashboardKeysForm.reset();
        this.router.navigate([this.navigatingTo]);
        this.confirmModal.closeModal();
    }

    onDecline(): void {
        this.confirmModal.closeModal();
    }
    navigatingTo: string;
}